import type { ReactNode } from 'react';
import type { GameDto } from '@/queries/games/games.schema';
import { SlotsFilledAliases } from '@/consts/seo/SlotsAliases';
import { isStaging } from '@/libs/getEnvironment';

export const COMPARE_GAME_LIST = isStaging() ? ['le-cowboy', 'best-game', 'super-50-stars1', 'china-pots-test-seo'] : SlotsFilledAliases;

type GameDetailsInfo = NonNullable<GameDto['details']>;

type CompareRow = {
  key: keyof GameDetailsInfo | 'rating';
  labelKey: string;
};

export const compareRows: CompareRow[] = [
  // TODO: add rating to game details and remove hardcoded rating row when it's available
  // { key: 'rating', labelKey: 'game-details.compare-rows.rating' },
  { key: 'category', labelKey: 'game-details.compare-rows.category' },
  { key: 'rtpType', labelKey: 'game-details.compare-rows.rtp-type' },
  { key: 'rtpRange', labelKey: 'game-details.compare-rows.rtp-range' },
  { key: 'rtp', labelKey: 'game-details.compare-rows.rtp' },
  { key: 'topPrize', labelKey: 'game-details.compare-rows.top-prize' },
  { key: 'topPrizeRange', labelKey: 'game-details.compare-rows.top-prize-range' },
  { key: 'volatility', labelKey: 'game-details.compare-rows.volatility' },
  { key: 'maxBet', labelKey: 'game-details.compare-rows.max-play' },
  { key: 'maxBetRange', labelKey: 'game-details.compare-rows.max-play-range' },
  { key: 'minBet', labelKey: 'game-details.compare-rows.min-play' },
  { key: 'minBetRange', labelKey: 'game-details.compare-rows.min-play-range' },
  { key: 'features', labelKey: 'game-details.compare-rows.features' },
  { key: 'themes', labelKey: 'game-details.compare-rows.themes' },
  { key: 'gameMechanics', labelKey: 'game-details.compare-rows.game-mechanics' },
  { key: 'promoTools', labelKey: 'game-details.compare-rows.promo-tools' },
  { key: 'jackpotType', labelKey: 'game-details.compare-rows.jackpot-type' },
];

export const hashString = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash);
};

export const getCompareGameAliases = (game?: Pick<GameDto, 'alias' | 'id'>) => {
  if (!game?.id) {
    return [];
  }

  return COMPARE_GAME_LIST
    .filter(alias => alias !== game.alias)
    .sort((first, second) => hashString(`${game.id}-${first}`) - hashString(`${game.id}-${second}`))
    .slice(0, 3);
};

export const getCellValue = (game: GameDto, rowKey: CompareRow['key']): ReactNode => {
  const value = game.details?.[rowKey as keyof GameDetailsInfo];

  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(', ') : '—';
  }

  if (rowKey === 'rtp' && typeof value === 'number') {
    return `${value}%`;
  }

  if (typeof value === 'string' && value.length > 0) {
    return value.trim() || '—';
  }

  return '—';
};
